import { AccessTime } from "@mui/icons-material"
import { Paper, Stack, styled, Typography } from "@mui/material"
import Box from "@mui/material/Box"
import Slider from "@mui/material/Slider"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { DeliveryConstraintEnum } from "src/features/config-setting/constants"
import { TooltipWrapper } from "src/features/shared-module/components"
import {
  getCalculatedTime,
  getHourFormat,
} from "src/features/shared-module/functions"
import { Parcel } from "src/features/shared-module/icons"

const StyledSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== "ownerState",
})(({ theme }) => ({
  height: 8,
  "&.MuiSlider-root": {
    padding: 0,
  },
  "& > .MuiSlider-thumb": {
    display: "none",
  },
  "&.Mui-disabled": {
    color: theme.palette.text.secondary,
    pointerEvents: "initial",
    cursor: "initial",
  },
  "& .MuiSlider-rail": {
    backgroundColor: theme.palette.secondary.light,
    opacity: 1,
  },
  "& .MuiSlider-track": {
    backgroundColor: theme.palette.primary.main,
    border: "none",
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#ffffff",
    height: 3,
    width: 3,
  },
}))

const marks = [
  { value: 4 },
  { value: 8 },
  { value: 12 },
  { value: 16 },
  { value: 20 },
]

export const OrderDeliveryProgress: FC<{
  deliveryConstraintType: DeliveryConstraintEnum
  isOnTimeDelivery: boolean
  start: string | null
  end: string | null
  calculatedTime: string | null
}> = ({
  start,
  end,
  calculatedTime,
  isOnTimeDelivery,
  deliveryConstraintType,
}) => {
  const { t } = useTranslation()
  const calculatedStart = getCalculatedTime(start) || 0
  const calculatedEnd = getCalculatedTime(end) || 24

  if (deliveryConstraintType === DeliveryConstraintEnum.TEXT_VERSION) {
    return (
      <Box
        data-testid="order-delivery-text"
        sx={() => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 28,
        })}
      >
        {calculatedTime && (
          <Typography variant="subtitle1" color="secondary" fontWeight={500}>
            {t("etaLabel")}: {getHourFormat(calculatedTime)}
          </Typography>
        )}
        {(start || end) && (
          <Box
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              paddingY: 0.25,
              paddingX: 1,
              borderRadius: 1,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.dark,
              marginLeft: "auto",
            })}
          >
            <DeliveryTimeRange start={start} end={end} />
          </Box>
        )}
      </Box>
    )
  }

  const timingList = [calculatedStart, calculatedEnd]
  let percentage = null
  if (calculatedTime !== null) {
    const arrivalTimeFormat = getHourFormat(calculatedTime)
    percentage = (100 * (getCalculatedTime(arrivalTimeFormat) || 0)) / 24
  }

  return (
    <TooltipWrapper
      placement="bottom"
      tooltipProps={{
        disableHoverListener: start === null && end === null,
        disableFocusListener: start === null && end === null,
      }}
      title={
        (start || end) && (
          <DeliveryTimeRange start={start} end={end} isTooltip={true} />
        )
      }
    >
      <Box sx={{ position: "relative" }}>
        <StyledSlider
          data-testid="order-delivery-slider"
          disabled={true}
          valueLabelDisplay="auto"
          valueLabelFormat={calculatedTime ? getHourFormat(calculatedTime) : ""}
          min={0}
          max={24}
          value={timingList}
          marks={marks}
        />
        {calculatedTime !== null && (
          <TooltipWrapper arrow={true} title={getHourFormat(calculatedTime)}>
            <Parcel
              sx={(theme) => ({
                color: isOnTimeDelivery
                  ? theme.palette.secondary.main
                  : theme.palette.error.light,
                height: 18,
                width: 18,
                position: "absolute",
                left: `${percentage}%`,
                transform: "translateX(-50%)",
                top: 4,
              })}
            />
          </TooltipWrapper>
        )}
      </Box>
    </TooltipWrapper>
  )
}

const DeliveryTimeRange = ({ start, end, isTooltip = false }: any) => {
  return (
    <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
      <AccessTime sx={{ fontSize: 14 }} />
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ fontSize: isTooltip ? "0.75rem" : "0.7rem", lineHeight: 1.2 }}
      >
        {start && end
          ? `${start.substring(0, 5)} – ${end.substring(0, 5)}`
          : start
          ? `from ${start.substring(0, 5)}`
          : end
          ? `before ${end.substring(0, 5)}`
          : ""}
      </Typography>
    </Stack>
  )
}
